import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, CheckCircle2, Sparkles, Award, Users, Rocket, ShieldCheck } from 'lucide-react';
import Reveal from '../components/Reveal';
import SectionHeader from '../components/SectionHeader';
import { IMAGES } from '../mock/data';

const VALUES = [
  { icon: Rocket, title: 'Speed with Precision', desc: 'First qualified shortlist within 48 hours — vetted, relevant, ready to interview.' },
  { icon: ShieldCheck, title: 'Vetted for Fit', desc: 'Deep technical screening and reference checks before any candidate reaches your inbox.' },
  { icon: Users, title: 'Consultative, Not Transactional', desc: 'We become an extension of your talent function — not just another vendor.' },
  { icon: Award, title: 'Long-Term Partnerships', desc: '95% retention after year one. We hire for careers, not just paychecks.' },
];

const About = () => (
  <>
    <section className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="hero-glow" />
      <div className="container-x relative">
        <Reveal>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/5 text-cyan-300 text-xs font-medium uppercase tracking-widest mb-6">
            <Sparkles className="w-3.5 h-3.5" /> About The Tech Reach
          </div>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-white leading-[1.05] max-w-4xl">
            Bridging the gap between <span className="shimmer-text">talent and opportunity.</span>
          </h1>
        </Reveal>
        <Reveal delay={160}>
          <p className="text-white/60 text-lg max-w-2xl mt-6">
            The Tech Reach is an IT recruitment agency delivering fast, accurate matchmaking for the world's most ambitious companies — from Fortune 1000 tech giants to scaling startups.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="section">
      <div className="container-x">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl overflow-hidden aspect-[3/4] border border-white/10">
                <img src={IMAGES.aboutB} alt="Team" className="w-full h-full object-cover" />
              </div>
              <div className="rounded-2xl overflow-hidden aspect-[3/4] border border-white/10 translate-y-8">
                <img src={IMAGES.hiringA} alt="Hiring" className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
          <div>
            <SectionHeader
              eyebrow="Our story"
              title="We don't just fill positions — we"
              accent="build futures."
              subtitle="With expertise in IT recruitment and staffing, we help individuals achieve career goals while enabling businesses to scale with confidence. Our mission is simple: bridge the gap between talent and opportunity."
            />
            <Reveal delay={200}>
              <div className="mt-8 grid grid-cols-2 gap-6">
                {[
                  { v: '500+', l: 'Placements delivered' },
                  { v: '15 days', l: 'Avg. time to hire' },
                  { v: '95%', l: '1-year retention' },
                  { v: '48h', l: 'First shortlist' },
                ].map((s, i) => (
                  <div key={i} className="border-l border-cyan-400/30 pl-4">
                    <div className="font-display text-3xl font-bold text-white">{s.v}</div>
                    <div className="text-xs uppercase tracking-wider text-white/50 mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    <section className="section">
      <div className="container-x">
        <SectionHeader align="center" eyebrow="What we stand for" title="Values that" accent="shape every placement." />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {VALUES.map((v, i) => (
            <Reveal key={i} delay={i * 100}>
              <div className="tilt-card glass rounded-2xl p-7 h-full">
                <div className="w-12 h-12 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center text-cyan-300 mb-5">
                  <v.icon className="w-6 h-6" />
                </div>
                <h3 className="font-display text-lg font-semibold text-white mb-2">{v.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="section">
      <div className="container-x">
        <div className="relative rounded-3xl overflow-hidden border border-white/10 p-10 md:p-14 text-center">
          <div className="absolute inset-0">
            <img src={IMAGES.netB} alt="" className="w-full h-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#05070E]/90 to-[#05070E]/70" />
          </div>
          <div className="relative max-w-2xl mx-auto">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white">Let's build your team together.</h2>
            <p className="text-white/70 mt-4">Talk to our team about your hiring needs — shortlist ready in 48 hours.</p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-1.5 px-6 py-3.5 rounded-full text-sm mt-7">
              Start a conversation <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  </>
);

export default About;
